// Pipeline stage definitions. Order matters — this is the canonical sequence
// vehicles flow through from acquisition to lot-ready.
//
// Each stage has:
//   id        — Firestore-safe slug, also used in routes
//   name      — display label
//   slaHours  — default SLA (a vehicle in this stage longer than this is flagged)
//               tenants can override per-stage in /tenants/{id}/stores/{id}.slaConfig
//   role      — minimum role required to *move a vehicle into* this stage
// Each stage has an optional exitChecklist. Required items must be ✓ before a
// vehicle can leave the stage (the advanceVehicleStage callable enforces this
// server-side too). Admins can override with a documented reason.
export const STAGES = [
  { id: 'acquisition',  name: 'Acquisition',     slaHours: 24,  role: 'service-manager',
    exitChecklist: [
      { id: 'intake-photos',  label: 'Intake photos taken',          required: true  },
      { id: 'vin-confirmed',  label: 'VIN matches title',            required: true  },
      { id: 'keys-accounted', label: 'Keys accounted for',           required: false },
  ] },
  { id: 'inspection',   name: 'Inspection',      slaHours: 48,  role: 'technician',
    exitChecklist: [
      { id: 'inspection-done',  label: '72-point inspection complete', required: true },
      { id: 'estimate-written', label: 'Estimate written',             required: true },
  ] },
  { id: 'mechanical',   name: 'Mechanical',      slaHours: 72,  role: 'technician',
    exitChecklist: [
      { id: 'oil',       label: 'Oil change',                          required: false },
      { id: 'brakes',    label: 'Brakes measured / serviced',          required: true },
      { id: 'road-test', label: 'Road test signed off',                required: true },
  ] },
  { id: 'body-paint',   name: 'Body & Paint',    slaHours: 120, role: 'service-manager',
    exitChecklist: [
      { id: 'body-qa',  label: 'Body shop QA pass',                    required: true },
  ] },
  { id: 'detail',       name: 'Detail',          slaHours: 24,  role: 'technician',
    exitChecklist: [
      { id: 'wash',       label: 'Wash + interior',                    required: true },
      { id: 'tire-shine', label: 'Tires dressed',                      required: false },
  ] },
  { id: 'photos',       name: 'Photos',          slaHours: 24,  role: 'technician',
    exitChecklist: [
      { id: 'photos-uploaded', label: 'Photos uploaded to lot tool',   required: true },
  ] },
  { id: 'pricing',      name: 'Pricing',         slaHours: 12,  role: 'service-manager',
    exitChecklist: [
      { id: 'price-set', label: 'Retail price set',                    required: true },
  ] },
  { id: 'frontline',    name: 'Frontline-ready', slaHours: 12,  role: 'service-manager',
    exitChecklist: [],
  },
];

// Acquisition source channels (used in the ROI filter + reports).
export const ACQUISITION_SOURCES = [
  { id: 'trade-in',          label: 'Trade-in' },
  { id: 'auction',           label: 'Auction' },
  { id: 'lease-return',      label: 'Lease return' },
  { id: 'dealer-trade',      label: 'Dealer trade' },
  { id: 'fleet',             label: 'Fleet' },
  { id: 'customer-purchase', label: 'Customer purchase' },
  { id: 'off-street',        label: 'Off-street' },
  { id: 'other',             label: 'Other' },
];

// Title status states (used on VehicleDetail + frontline gate).
export const TITLE_STATUSES = [
  { id: 'in-hand',           label: 'In hand' },
  { id: 'pending',           label: 'Pending' },
  { id: 'missing',           label: 'Missing' },
  { id: 'duplicate-ordered', label: 'Duplicate ordered' },
];

export function stageById(id) {
  return STAGES.find((s) => s.id === id) || STAGES[0];
}

// Age-pill bucket for a vehicle in a stage. Returns 'ok' | 'warn' | 'over'.
// `enteredAt` accepts JS Date, Firestore Timestamp, ISO string, or epoch-ms.
export function ageBucket(enteredAt, stageId) {
  if (!enteredAt) return 'ok';
  const ms = enteredAt.toMillis
    ? enteredAt.toMillis()
    : enteredAt instanceof Date
    ? enteredAt.getTime()
    : new Date(enteredAt).getTime();
  if (Number.isNaN(ms)) return 'ok';
  const hours = (Date.now() - ms) / 36e5;
  const stage = stageById(stageId);
  if (hours > stage.slaHours) return 'over';
  if (hours > stage.slaHours * 0.7) return 'warn';
  return 'ok';
}

// Human-readable elapsed time. Below 1h shows minutes, below 24h shows hours,
// otherwise days. The earlier "< 1d" lump-bucket made an 18-hour-old vehicle
// feel as fresh as a brand-new one which it isn't. Tolerates JS Date,
// Firestore Timestamp, ISO string, and epoch-ms number.
export function ageLabel(enteredAt) {
  if (!enteredAt) return '—';
  const ms = enteredAt.toMillis
    ? enteredAt.toMillis()
    : enteredAt instanceof Date
    ? enteredAt.getTime()
    : new Date(enteredAt).getTime();
  if (Number.isNaN(ms)) return '—';
  const hours = (Date.now() - ms) / 36e5;
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60));
    return `${minutes}m`;
  }
  if (hours < 24) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}
